/**
 * PageHelpButton.tsx — the circular "?" button pinned to a page header's
 * top-right corner. Opens that page's PageHelpDialog (an illustrated,
 * interactive guide defined in components/help/content/).
 *
 * Attention affordance: a soft pulse animation on the FIRST ever visit to the
 * page (per browser — localStorage `belab.pageHelp.seen.<pageKey>`), which
 * stops permanently once the guide has been opened. Users who prefer reduced
 * motion never see the pulse.
 *
 * The dialog mounts only after the first open — pages don't pay for the
 * guide's content tree until someone asks for it.
 */

import { useState } from 'react';
import { IconButton, Tooltip, alpha, useTheme } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { HELP_CONTENT } from './content';
import type { PageHelpKey } from './helpTypes';
import { PageHelpDialog } from './PageHelpDialog';
import { hasSeenPageHelp, markPageHelpSeen } from './pageHelpStorage';

interface PageHelpButtonProps {
  pageKey: PageHelpKey;
}

export const PageHelpButton = ({ pageKey }: PageHelpButtonProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  /** True once the dialog has been opened at least once — gates the mount. */
  const [mounted, setMounted] = useState(false);
  const [seen, setSeen] = useState(() => hasSeenPageHelp(pageKey));

  const handleOpen = () => {
    markPageHelpSeen(pageKey);
    setSeen(true);
    setMounted(true);
    setOpen(true);
  };

  return (
    <>
      <Tooltip title="What is this page? Open the guide">
        <IconButton
          aria-label="Open the page guide"
          onClick={handleOpen}
          size="small"
          sx={{
            color: theme.palette.primary.main,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.14) },
            ...(!seen && {
              animation: 'belabHelpPulse 2.2s ease-in-out infinite',
              '@keyframes belabHelpPulse': {
                '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.4)}` },
                '70%': { boxShadow: `0 0 0 9px ${alpha(theme.palette.primary.main, 0)}` },
                '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }),
          }}
        >
          <HelpOutline fontSize="small" />
        </IconButton>
      </Tooltip>
      {mounted && (
        <PageHelpDialog
          open={open}
          onClose={() => setOpen(false)}
          content={HELP_CONTENT[pageKey]}
        />
      )}
    </>
  );
};
